/**
 * Validation utilities for FlatBuffer schema editing.
 *
 * Ensures enum/union changes follow FlatBuffers compatibility rules:
 *   - PascalCase names
 *   - No duplicates
 *   - Append-only (no reordering or deleting)
 *   - Backing type range limits
 */

export interface ValidationError {
  field: string;
  message: string;
}

/** Maximum values allowed per backing integer type */
const BACKING_TYPE_MAX: Record<string, number> = {
  uint8: 255,
  int8: 127,
  uint16: 65535,
  int16: 32767,
  uint32: 4294967295,
  int32: 2147483647,
};

/**
 * Validates a single enum value name.
 * Rules: must be PascalCase (start with uppercase letter), only
 * alphanumeric and underscores, and not empty.
 */
export function validateEnumValueName(name: string): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!name || name.trim().length === 0) {
    errors.push({ field: "name", message: "Name cannot be empty" });
    return errors;
  }

  if (!/^[A-Z]/.test(name)) {
    errors.push({
      field: "name",
      message: `"${name}" must start with an uppercase letter (PascalCase)`,
    });
  }

  if (!/^[A-Z][a-zA-Z0-9_]*$/.test(name)) {
    errors.push({
      field: "name",
      message: `"${name}" contains invalid characters. Only letters, digits, and underscores are allowed.`,
    });
  }

  return errors;
}

/**
 * Validates adding new enum values to an existing enum.
 * Checks for duplicates and optionally validates backing type range.
 */
export function validateEnumAddition(
  existingValues: string[],
  newValues: string[],
  backingType?: string,
): ValidationError[] {
  const errors: ValidationError[] = [];
  const allNames = new Set(existingValues);

  newValues.forEach((name) => {
    // Check for duplicates against existing values and other new values
    if (allNames.has(name)) {
      errors.push({
        field: "duplicate",
        message: `"${name}" already exists in this enum`,
      });
    } else {
      allNames.add(name);
    }

    // Validate individual name format
    const nameErrors = validateEnumValueName(name);
    errors.push(...nameErrors);
  });

  // Check backing type range
  if (backingType) {
    const max = BACKING_TYPE_MAX[backingType];
    if (max !== undefined) {
      const totalCount = existingValues.length + newValues.length;
      if (totalCount > max) {
        errors.push({
          field: "range",
          message: `Adding ${newValues.length} value(s) would exceed the ${backingType} maximum of ${max}. Current count: ${existingValues.length}, after adding: ${totalCount}.`,
        });
      }
    }
  }

  return errors;
}

/**
 * Validates a single union member name.
 * Same rules as enum value names -- must be PascalCase.
 */
export function validateUnionMemberName(name: string): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!name || name.trim().length === 0) {
    errors.push({ field: "name", message: "Member name cannot be empty" });
    return errors;
  }

  // Union members can be qualified (e.g., Namespace.TypeName)
  const parts = name.split(".");
  const typeName = parts[parts.length - 1];

  if (!/^[A-Z]/.test(typeName)) {
    errors.push({
      field: "name",
      message: `"${typeName}" must start with an uppercase letter (PascalCase)`,
    });
  }

  if (!/^[A-Z][a-zA-Z0-9_]*$/.test(typeName)) {
    errors.push({
      field: "name",
      message: `"${typeName}" contains invalid characters. Only letters, digits, and underscores are allowed.`,
    });
  }

  return errors;
}

/**
 * Validates that a modified list does not reorder or delete entries
 * compared to the original. FlatBuffer compatibility requires append-only changes.
 */
export function validateNoReorderOrDelete(
  original: string[],
  modified: string[],
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (modified.length < original.length) {
    const removed = original.filter((v) => !modified.includes(v));
    errors.push({
      field: "deletion",
      message: `Cannot delete FlatBuffer values. Removed: ${removed.join(", ")}`,
    });
  }

  // Check that all original values appear in the same order
  for (let i = 0; i < original.length; i += 1) {
    if (i >= modified.length) {
      errors.push({
        field: "deletion",
        message: `Original value "${original[i]}" at index ${i} was removed`,
      });
    } else if (modified[i] !== original[i]) {
      errors.push({
        field: "reorder",
        message: `Value at index ${i} changed from "${original[i]}" to "${modified[i]}". Reordering is not allowed in FlatBuffers.`,
      });
      break; // Stop at first reorder to avoid cascading errors
    }
  }

  return errors;
}
